import React, { useState } from 'react';
import { Field, Form as FinalForm } from 'react-final-form';
import { Alert, Button, Container, Form, Input, InputGroup, InputGroupAddon } from 'reactstrap';
import OperationalIssuesPanel from '../../../../operationalIssues/OperationalIssuesPanel';

type SettingsSectionProperties =
{
    settings: any | null
    testResult: any | null
    lastError: string | null
    providerCredentials: any | null
    operationalIssues?: any[]
    onResolveOperationalIssue?: (key: string) => Promise<any>
    onSubmit: (values: any) => Promise<void> | void
    onTestConnection: (values: any) => Promise<any>
}

const defaultSettings =
{
    authMode: 'ServiceAccount',
    merchantAccountId: '',
    automaticSynchronizationEnabled: false
};

function FieldHelp
(
    {
        label,
        children
    }
    :
    {
        label: string
        children: React.ReactNode
    }
)
{
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className="mb-1">
            <div className="d-flex align-items-center">
                <span>{label}</span>
                <button
                    type="button"
                    className="btn btn-link btn-sm p-0 ml-2"
                    aria-label={`Pomoc: ${label}`}
                    onClick={() => setIsOpen(value => !value)}
                >
                    <i className="fa fa-question-circle" />
                </button>
            </div>

            {isOpen && (
                <Alert color="info" className="mt-2 mb-2 py-2">
                    {children}
                </Alert>
            )}
        </div>
    );
}

function MerchantAccountIdField()
{
    return (
        <Field name="merchantAccountId">
            {props => (
                <div>
                    <FieldHelp label="Merchant Account ID">
                        Merchant Account ID znajdziesz w Google Merchant Center. Po zalogowaniu otwórz Merchant Center,
                        a następnie kliknij nazwę lub identyfikator konta w górnym menu. Identyfikator konta jest widoczny
                        przy wybranym koncie Merchant Center.
                    </FieldHelp>
                    <Input
                        {...props.input}
                        type="text"
                        invalid={!!(props.meta.error || props.meta.submitError) && !!props.meta.touched}
                    />
                    {(props.meta.error || props.meta.submitError) && props.meta.touched && (
                        <small className="text-danger">{props.meta.error || props.meta.submitError}</small>
                    )}
                </div>
            )}
        </Field>
    );
}

function ProviderCredentialsSection
(
    {
        providerCredentials
    }
    :
    {
        providerCredentials: any | null
    }
)
{
    const [copied, setCopied] = useState(false);
    const serviceAccountEmail = providerCredentials?.serviceAccountEmail || '';
    const isConfigured = providerCredentials?.serviceAccountConfigured === true || providerCredentials?.configured === true;
    const hasServiceAccountEmail = !!serviceAccountEmail;

    const copyServiceAccountEmail = async () =>
    {
        if (!serviceAccountEmail)
        {
            return;
        }

        await navigator.clipboard.writeText(serviceAccountEmail);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1800);
    };

    return (
        <section className="mb-5">
            <div className="row">
                <div className="col-12 col-xl-6">
                    <FieldHelp label="Aplikacja Connectico w Google Merchant">
                        Dodaj powyższy adres jako użytkownika z dostępem <strong>Admin</strong> w Google Merchant Center.
                        Dzięki temu integracja będzie mogła tworzyć źródła danych i wysyłać produkty do wskazanego konta Merchant.
                    </FieldHelp>

                    <InputGroup>
                        <Input
                            type="text"
                            readOnly
                            value={serviceAccountEmail || (isConfigured ? 'Service account jest skonfigurowany po stronie systemu.' : 'Brak adresu service account.')}
                        />
                        {serviceAccountEmail && (
                            <InputGroupAddon addonType="append">
                                <Button
                                    type="button"
                                    color={copied ? 'success' : 'secondary'}
                                    title={copied ? 'Skopiowano' : 'Kopiuj adres'}
                                    onClick={copyServiceAccountEmail}
                                >
                                    <i className={copied ? 'fa fa-check' : 'fa fa-copy'} />
                                </Button>
                            </InputGroupAddon>
                        )}
                    </InputGroup>

                    {!hasServiceAccountEmail && !isConfigured && (
                        <small className="text-danger d-block mt-1">Skontaktuj się z administratorem systemu.</small>
                    )}
                </div>
            </div>
        </section>
    );
}

function SettingsSection
(
    {
        settings,
        testResult,
        lastError,
        providerCredentials,
        operationalIssues,
        onResolveOperationalIssue,
        onSubmit,
        onTestConnection
    }
    : SettingsSectionProperties
)
{
    const [isTesting, setIsTesting] = useState(false);

    const initialValues =
    {
        ...defaultSettings,
        ...(settings ?? {}),
        authMode: 'ServiceAccount'
    };

    const handleSubmitSettings = async (values: any) =>
    {
        await onSubmit
        ({
            ...(settings ?? {}),
            ...values,
            authMode: 'ServiceAccount',
            name: settings?.name || 'Google Merchant Center'
        });
    };

    return (
        <Container fluid>
            <FinalForm
                initialValues={initialValues}
                onSubmit={handleSubmitSettings}
                render={({ handleSubmit, values }) =>
                {
                    const handleTestConnection = async () =>
                    {
                        setIsTesting(true);

                        try
                        {
                            await onTestConnection({ ...values, authMode: 'ServiceAccount' });
                        }
                        finally
                        {
                            setIsTesting(false);
                        }
                    };

                    return (
                        <Form onSubmit={handleSubmit}>
                            <OperationalIssuesPanel
                                issues={operationalIssues}
                                title="Problemy połączenia Google Merchant"
                                onResolve={onResolveOperationalIssue}
                                compact={true}
                            />

                            <section className="mb-5">

                                <div className="row">
                                    <div className="col-12 col-xl-6">
                                        <div className="mb-3">
                                            <MerchantAccountIdField />
                                        </div>
                                    </div>
                                </div>
                            </section>

                            <ProviderCredentialsSection providerCredentials={providerCredentials} />

                            <div className="d-flex flex-wrap justify-content-end mt-4">
                                <Button type="button" color="secondary" className="mr-2 mb-2" disabled={isTesting} onClick={handleTestConnection}>
                                    Test połączenia
                                </Button>
                                <Button type="submit" color="primary" className="mb-2">
                                    Zapisz konfigurację
                                </Button>
                            </div>

                            {lastError && <Alert color="danger" className="mt-3">{lastError}</Alert>}

                            {testResult && (
                                <Alert color={testResult.success ? 'success' : 'danger'} className="mt-3">
                                    <strong>{testResult.success ? 'Połączenie działa.' : 'Test połączenia nie powiódł się.'}</strong>

                                    {testResult.message && <div>{testResult.message}</div>}

                                    {testResult.warnings && testResult.warnings.length > 0 && (
                                        <ul className="mt-3 mb-0">
                                            {testResult.warnings.map((warning: string, index: number) => (
                                                <li key={index}>{warning}</li>
                                            ))}
                                        </ul>
                                    )}

                                    {testResult.account && (
                                        <pre className="mt-3 mb-0">{JSON.stringify(testResult.account, null, 2)}</pre>
                                    )}
                                </Alert>
                            )}
                        </Form>
                    );
                }}
            />
        </Container>
    );
}

export default SettingsSection;
